import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import SelectBrandAndSubbrands from "components/ui/selectors/SelectBrandAndSubbrands";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import { useAllAgitems } from "http-hooks/agitem/useAgitem";
import { useGenerateAnalysisItemsReport } from "http-hooks/salebill/useSalebill";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import useAuth from "hooks/useAuth";

const reportTypeOptions = [
  { value: "P", label: "Purchases" },
  { value: "S", label: "Sales" },
];

const AnalysisItems = () => {
  const { agttp } = useAuth();
  const navigate = useNavigate();
  const { mutateAsync: generateReport, isLoading: isGenerating } = useGenerateAnalysisItemsReport();

  const [formData, setFormData] = useState({
    reportType: agttp === 1 ? "" : "G", // P or S if agttp=1, else 'G'
    vendor: null,
    vendorGroup: true, // checkbox, default checked
    itemGroup: false, // checkbox, default unchecked
    periodStart: "", // Will be set by useEffect
    periodEnd: "", // Will be set by Input component's now prop
    customers: [], // multiple
    brandsAndSubbrands: [], // multiple - combined brands and subbrands
    items: [], // multiple
  });

  // Period start will be set automatically by Input component's fys prop

  // Construct brandIds for API - combine both brands and subbrands
  const combinedBrandIds = useMemo(() => {
    return formData.brandsAndSubbrands.map(b => b.id).join(",");
  }, [formData.brandsAndSubbrands]);

  const { data: allItemsResponse, isLoading: isLoadingItems } = useAllAgitems(
    {
      brandIds: combinedBrandIds,
      noPagination: true,
    },
    formData.brandsAndSubbrands.length > 0
  );

  // Transform items for Select component
  const itemOptions = useMemo(() => {
    const allItems = allItemsResponse?.data || [];
    return allItems.map(item => {
      // Format: BRAND SUBBRAND CODE ITEMNAME
      const parts = [
        item.brand || '',
        item.subbrandname || '',
        item.itemCode || '',
        item.itemName || ''
      ].filter(part => part); // Remove empty parts
      
      return {
        value: item.itemId,
        label: parts.join(' '),
        ...item
      };
    });
  }, [allItemsResponse]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset dependent fields when parent changes
    if (name === "vendor") {
      setFormData((prev) => ({
        ...prev,
        brandsAndSubbrands: [],
        items: [],
      }));
    } else if (name === "brandsAndSubbrands") {
      setFormData((prev) => ({
        ...prev,
        items: [],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (agttp === 1 && !formData.reportType) {
      alert("Please select Report Type");
      return;
    }
    if (agttp !== 1) {
      // For non-trader (agttp != 1), validate vendor, brands/subbrands, items
      if (!formData.vendor) {
        alert("Please select Vendor");
        return;
      }
      if (formData.brandsAndSubbrands.length === 0) {
        alert("Please select at least one Brand/Sub Brand");
        return;
      }
      if (formData.items.length === 0) {
        alert("Please select at least one Item");
        return;
      }
    }

    // Prepare report data
    const brands = formData.brandsAndSubbrands.filter(b => b.level === 1).map(b => b.id);
    const subbrands = formData.brandsAndSubbrands.filter(b => b.level === 2).map(b => b.id);
    const itemIds = formData.items.map(item => item.value);
    const customerIds = formData.customers.map(cust => cust.ps_Id);

    const reportData = {
      reportType: formData.reportType,
      vendorId: formData.vendor?.ps_Id,
      vendorGroup: formData.vendorGroup,
      itemGroup: formData.itemGroup,
      periodStart: formData.periodStart,
      periodEnd: formData.periodEnd,
      customerIds: customerIds.length > 0 ? customerIds : null,
      brandIds: brands,
      subbrandIds: subbrands,
      itemIds: itemIds,
    };

    const data = await generateReport(reportData);
    
    const { htmlUrl, pdfUrl } = getDocumentUrls(data);
    const route = getDocumentViewerRoute("analysis-items", {
      htmlUrl,
      pdfUrl,
      documentNumber: `${formData.periodStart} to ${formData.periodEnd}`,
      documentType: "Analysis (Items) Report",
    });
    navigate(route);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            Item Details Report
          </h1>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit} className="px-4 py-6 sm:p-8">
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* Report Type - Only show if agttp === 1 (Trader) */}
            {agttp === 1 && (
              <div className="sm:col-span-3">
                <Select
                  label="Report Type *"
                  options={reportTypeOptions}
                  value={formData.reportType}
                  onChange={(value) => handleInputChange("reportType", value)}
                  placeholder="Choose here"
                />
              </div>
            )}

            {/* For non-traders (agttp !== 1), show vendor and other fields */}
            {agttp !== 1 && (
              <>
                {/* Vendor - Full Row */}
                <div className="sm:col-span-6">
                  <div className="max-w-lg">
                    <SelectVendors
                      label="Vendor *"
                      value={formData.vendor}
                      onChange={(value) => handleInputChange("vendor", value)}
                      isClearable={false}
                    />
                  </div>
                </div>

                {/* Checkboxes - Both in One Row */}
                <div className="sm:col-span-3">
                  <Input
                    type="checkbox"
                    label="Vendor Group"
                    value={formData.vendorGroup}
                    onChange={(e) =>
                      handleInputChange("vendorGroup", e.target.checked)
                    }
                  />
                </div>

                <div className="sm:col-span-3">
                  <Input
                    type="checkbox"
                    label="Item Group"
                    value={formData.itemGroup}
                    onChange={(e) =>
                      handleInputChange("itemGroup", e.target.checked)
                    }
                  />
                </div>

                {/* Period Dates - Both in One Row */}
                <div className="sm:col-span-3">
                  <Input
                    label="Period Start *"
                    type="date"
                    value={formData.periodStart}
                    onChange={(e) =>
                      handleInputChange("periodStart", e.target.value)
                    }
                    fys={true}
                    withinFY={true}
                    required
                  />
                </div>

                <div className="sm:col-span-3">
                  <Input
                    label="Period End *"
                    type="date"
                    value={formData.periodEnd}
                    onChange={(e) => handleInputChange("periodEnd", e.target.value)}
                    now={true}
                    withinFY={true}
                    required
                  />
                </div>

                {/* Customers */}
                <div className="sm:col-span-3">
                  <SelectCustomers
                    label="Customer"
                    value={formData.customers}
                    onChange={(value) => handleInputChange("customers", value)}
                    isMulti={true}
                  />
                </div>

                {/* Empty space for alignment */}
                <div className="sm:col-span-3"></div>

                {/* Brands and Sub-brands (conditionally shown after vendor selection) */}
                {formData.vendor && (
                  <div className="sm:col-span-3">
                    <SelectBrandAndSubbrands
                      label="Brand/Sub Brand *"
                      value={formData.brandsAndSubbrands}
                      onChange={(value) => handleInputChange("brandsAndSubbrands", value)}
                      vendorId={formData.vendor?.ps_Id}
                      isMulti={true}
                      showAddButton={false}
                    />
                  </div>
                )}

                {/* Items (conditionally shown after brand/subbrand selection) */}
                {formData.brandsAndSubbrands.length > 0 && (
                  <div className="sm:col-span-3">
                    <Select
                      label="Item *"
                      value={formData.items}
                      onChange={(value) => handleInputChange("items", value)}
                      options={itemOptions}
                      placeholder="Select items..."
                      isMulti={true}
                      isClearable={true}
                      isSearchable={true}
                      isLoading={isLoadingItems}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="submit"
              disabled={isGenerating}
              className="rounded-md bg-indigo-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? "Generating..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnalysisItems;

